/**
 * CampaignQueueStatusCards - Cards de KPIs da fila de campanhas
 * 
 * Exibe métricas principais: pendentes, processando, enviados, falhas, taxa de sucesso
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, Send, CheckCircle2, XCircle, Percent, Timer, Hourglass, Loader2 } from 'lucide-react';
import type { CampaignQueueStats } from '@/hooks/useCampaignQueueMonitor';

interface CampaignQueueStatusCardsProps {
  stats: CampaignQueueStats | undefined;
  isLoading: boolean;
}

export function CampaignQueueStatusCards({ stats, isLoading }: CampaignQueueStatusCardsProps) {
  // Formatar tempo de processamento
  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  };

  const cards = [
    {
      title: 'Pendentes',
      value: stats?.pending_count || 0,
      icon: Clock,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10'
    },
    {
      title: 'Na Fila',
      value: stats?.queued_count || 0,
      icon: Hourglass,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      title: 'Processando',
      value: (stats?.processing_count || 0) + (stats?.sending_count || 0),
      icon: Loader2,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10'
    },
    {
      title: 'Enviados (24h)',
      value: (stats?.sent_count || 0) + (stats?.delivered_count || 0) + (stats?.read_count || 0),
      icon: Send,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10'
    },
    {
      title: 'Falhas (24h)',
      value: stats?.failed_count || 0,
      icon: XCircle,
      color: 'text-destructive',
      bgColor: 'bg-destructive/10'
    },
    {
      title: 'Taxa Sucesso',
      value: `${stats?.success_rate || 100}%`,
      icon: Percent,
      color: (stats?.success_rate || 100) >= 90 ? 'text-green-500' : 'text-amber-500',
      bgColor: (stats?.success_rate || 100) >= 90 ? 'bg-green-500/10' : 'bg-amber-500/10'
    },
    {
      title: 'Tempo Médio',
      value: formatTime(stats?.avg_processing_time_ms || 0),
      icon: Timer,
      color: 'text-muted-foreground',
      bgColor: 'bg-muted'
    },
    {
      title: 'Ignorados',
      value: stats?.skipped_count || 0,
      icon: CheckCircle2,
      color: 'text-muted-foreground',
      bgColor: 'bg-muted'
    }
  ];

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-4 lg:grid-cols-8">
        {Array.from({ length: 8 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-4 rounded-full" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-7 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-4 lg:grid-cols-8">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              {card.title}
            </CardTitle>
            <div className={`p-1.5 rounded-md ${card.bgColor}`}>
              <card.icon className={`h-3.5 w-3.5 ${card.color}`} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">{card.value.toLocaleString()}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
